<template>
  <v-main class="pt-0 pb-4 mb-4">
    <v-container class="maxContainerWidth mb-16">
      <!-- My Primerica Header -->

      <v-row class="text-white mt-4 mb-4">
        <v-col
          class="d-flex flex-wrap"
          :class="$vuetify.display.xs ? 'align-start' : 'align-center'"
        >
          <v-icon color="#d2dee9" :icon="mdiCalendarRange" alt="Calendar Range Icon" size="48" />
          <h1 class="text-white pl-2 text-wrap" :class="$vuetify.display.xs ? 'w-75' : 'w-auto'">
            {{ $t('L_update_monthly_withdrawal_day') }}
          </h1>
          <v-spacer v-if="!$vuetify.display.smAndDown"></v-spacer>
          <!-- Policy Number -->
          <h3 class="text-white" :class="$vuetify.display.smAndDown ? 'ml-10 pl-4' : 'ml-0 pl-0'">
            {{ $t('L_policy_policy_number') }}:
            <span class="font-weight-regular">{{ currentPolicyNumber }}</span>
          </h3>
        </v-col>
      </v-row>

      <MessageDialog
        :show-dialog="dialog"
        :has-button-two="true"
        type="warning"
        :title="$t('L_cancel_withdrawal_day_change')"
        :message="$t('L_do_you_want_to_cancel_the_changes_to_withdrawal_day')"
        :button-two-label="$t('L_yes_cancel')"
        :button-one-label="$t('L_no_continue')"
        :is-persistent="true"
        @buttonTwoAction="closeAndGoToPolicyDetails"
        @buttonOneAction="dialog = false"
      />
      <!-- Section Guide
      One Time Payment -    1 Payment Amount  2 Payment Method  3 Welcome Back
      ACH              -    1 Payment Amount  2 Payment Method  3 Review Payment 4 Confirmation
      -->
      <!-- This is for screen larger than xsOnly Horizontal Mode -->
      <v-stepper v-model="step" :vertical="$vuetify?.display?.xs">
        <v-stepper-header v-show="!$vuetify?.display?.xs">
          <v-stepper-item :complete="step === '2' || step === '3'" value="1">
            {{ $t('L_select_day') }}
          </v-stepper-item>
          <v-divider />
          <v-stepper-item :complete="step === '3'" value="2">
            {{ $t('L_review') }}
          </v-stepper-item>
          <v-divider />
          <v-stepper-item :complete="step === '4'" value="3">
            {{ $t('L_confirmation') }}
          </v-stepper-item>
        </v-stepper-header>

        <v-stepper-window class="ma-3">
          <v-stepper-item
            :complete="step === '2' || step === '3'"
            value="1"
            v-show="$vuetify?.display?.xs"
            class="pa-0 mx-0 mt-2 mb-2"
            :class="{
              'my-step-complete': step === '1',
              'border-complete': $vuetify.display.xs && (step === '2' || step === '3'),
            }"
          >
            {{ $t('L_select_day') }}
          </v-stepper-item>
          <v-stepper-window-item
            value="1"
            :class="{ 'border-s-thin border-opacity ml-3': $vuetify.display.xs }"
          >
            <WDStepOne
              @closeAndGoToPolicyDetails="closeAndGoToPolicyDetails"
              @goToWDStepTwo="goToWDStepTwo"
              @cancelDialog="dialog = true"
              @selectDateIssueNextPayDateEarly="selectDateIssueNextPayDateEarly"
              @setDateForNextPayment="setDateForNextPayment"
            />
          </v-stepper-window-item>
          <v-stepper-item
            :complete="step === '3'"
            value="2"
            v-show="$vuetify?.display?.xs"
            class="pa-0 mx-0 mt-2 mb-2"
            :class="{
              'my-step-complete': step === '2',
              'border-disabled': $vuetify.display.xs && step === '1',
              'border-complete': $vuetify.display.xs && step === '3',
            }"
          >
            {{ $t('L_review') }}
          </v-stepper-item>
          <v-stepper-window-item
            value="2"
            :class="{ 'border-s-thin border-opacity ml-3': $vuetify.display.xs }"
          >
            <WDStepTwo
              :day-selected="daySelected"
              :date-issue-next-pay-date-early="dateIssueNextPayDateEarly"
              :date-for-next-payment="dateForNextPayment"
              @cancelDialog="dialog = true"
              @goToWDStepThree="goToWDStepThree"
              @goBackToWDStepOne="goBackToWDStepOne"
            />
          </v-stepper-window-item>

          <v-stepper-item
            :complete="step === '4'"
            value="3"
            v-show="$vuetify?.display?.xs"
            class="pa-0 mx-0 mt-2 mb-2"
            :class="{
              'my-step-complete': step === '3',
            }"
          >
            {{ $t('L_confirmation') }}
          </v-stepper-item>
          <v-stepper-window-item
            value="3"
            :class="{ 'border-s-thin border-opacity ml-3': $vuetify.display.xs }"
          >
            <WDStepThree
              :day-selected="daySelected"
              :date-issue-next-pay-date-early="dateIssueNextPayDateEarly"
              :date-for-next-payment="dateForNextPayment"
              @cancelDialog="dialog = true"
            />
          </v-stepper-window-item>
        </v-stepper-window>
      </v-stepper>
      <FooterComponent />
    </v-container>
    <NavigationConfirmationDialog
      v-if="showNavigationConfirmationDialog"
      @confirm-navigation="navigateToRoute"
      @cancel-navigation="showNavigationConfirmationDialog = false"
    />
  </v-main>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import { currency } from '@/shared/util/formatters';
import WDStepOne from './components/WDStepOne.vue';
import WDStepTwo from './components/WDStepTwo.vue';
import WDStepThree from './components/WDStepThree.vue';
import MessageDialog from '@/components/MessageDialog.vue';
import { mapState, mapActions } from 'pinia';
import { useCustomerStore } from '@/stores/customer/customer.store';
import { usePolicyStore } from '@/modules/policy/store/policy.store';
import { useGlobalStore } from '@/stores/global/global.store';
import { RouteNames } from '@/router/routeNames';
import FooterComponent from '@/components/Footer/Footer.vue';
import { mdiCalendarRange } from '@mdi/js';
import NavigationConfirmationDialog from '@/components/NavigationConfirmation/NavigationConfirmationDialog.vue';
import type { RouteParamsGeneric } from 'vue-router';

export default defineComponent({
  name: 'WithDrawalDate',
  components: {
    WDStepOne,
    WDStepTwo,
    WDStepThree,
    MessageDialog,
    FooterComponent,
    NavigationConfirmationDialog,
  },
  data: () => ({
    persistent: true,
    dialog: false,
    step: '1',
    daySelected: '1',
    dateIssueNextPayDateEarly: -1,
    dateForNextPayment: '',
    mdiCalendarRange,
    currentPolicyNumber: '',
    showNavigationConfirmationDialog: false,
    navigatingToRoute: '',
    navigatingToParams: {} as RouteParamsGeneric,
    navigationAwayConfirmed: false,
  }),
  computed: {
    ...mapState(useCustomerStore, ['customerProfile']),
    ...mapState(usePolicyStore, [
      'currentPolicy',
      'selectedPolicyIndex',
      'paymentData',
      'withDrawalDateStep',
    ]),
    ...mapState(useGlobalStore, { locale: 'getLocale' }),
    getTotalDue() {
      return currency(+this.currentPolicy?.paynowInfo?.totalPaymentAmount, 2);
    },
    getMinimumDue() {
      return currency(+this.currentPolicy?.paynowInfo?.minimumPaymentAmount, 2);
    },
    getPolicyNum() {
      return this.currentPolicy?.contractId || '';
    },
  },
  methods: {
    ...mapActions(usePolicyStore, {
      setWithDrawalDateStep: 'setWithDrawalDateStep',
    }),
    navigateToRoute() {
      this.navigationAwayConfirmed = true;
      void this.$router.push({
        name: this.navigatingToRoute,
        params: this.navigatingToParams,
      });
    },
    async closeAndGoToPolicyDetails() {
      this.navigationAwayConfirmed = true;
      this.setWithDrawalDateStep(-1);
      await this.$router.push({
        name: RouteNames.POLICY_DETAILS,
        params: { contractId: this.selectedPolicyIndex },
      });
    },
    goToWDStepTwo(WithDrawalSelectedDay: number) {
      this.daySelected = WithDrawalSelectedDay + '';
      this.step = '2';
      this.setWithDrawalDateStep(2);
    },
    goToWDStepThree() {
      this.step = '3';
      this.setWithDrawalDateStep(3);
    },
    goBackToWDStepOne() {
      this.step = '1';
      this.setWithDrawalDateStep(1);
    },
    selectDateIssueNextPayDateEarly(val: number) {
      this.dateIssueNextPayDateEarly = val;
    },
    setDateForNextPayment(val: string) {
      this.dateForNextPayment = val;
    },
  },
  mounted() {
    this.currentPolicyNumber = this.currentPolicy?.contractId || '';
  },
  beforeRouteLeave(to, _, next) {
    const isProcessDone = this.withDrawalDateStep === -1 || this.navigationAwayConfirmed;
    // const navigatingOutsideMinorAccountLink =
    //   to.name !== RouteNames.MINOR_ACCOUNT_LINK_PROCESS &&
    //   to.path !== '/minor-account-link-process';
    // navigatingOutsideMinorAccountLink &&
    if (!isProcessDone) {
      this.showNavigationConfirmationDialog = true; // show NavigationConfirmationDialog
      this.navigatingToRoute = to.name as string; // save the route name
      this.navigatingToParams = to.params;
      next(false); // cancel the navigation
    } else {
      this.setWithDrawalDateStep(-1); // reset the step when navigation happens
      next();
    }
  },
});
</script>

<style lang="scss" scoped>
@media only screen and (max-width: 959px) {
  .v-stepper:not(.v-stepper--vertical) .v-stepper__label {
    display: block !important;
    padding-left: 5px;
  }
}

.maxContainerWidth {
  max-width: 1024px;
  margin: auto;
}

.v-stepper__header {
  overflow: auto;
  display: flex !important;
  flex-wrap: no-wrap;
  justify-content: left;
}

.border-disabled[disabled].v-stepper-item {
  position: relative;
  padding-bottom: 8px !important;
}
.border-disabled[disabled].v-stepper-item::before,
.border-complete.v-stepper-item--complete::before {
  content: '|';
  color: rgba(var(--v-theme-black));
  opacity: 0.12;
  position: absolute;
  padding-top: 8px;
  left: 8px;
  top: 15px;
}

.dollarSignBig {
  font-size: 36px;
}

.dollarSignSmall {
  font-size: 28px;
}

.dollarSignIconBig {
  font-size: 56px;
}

.dollarSignIconSmall {
  font-size: 36px;
}
</style>
